import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const LoginForm = ({ currentLanguage }) => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const translations = {
    en: {
      title: 'Sign In',
      subtitle: 'Enter your credentials to access your account',
      email: 'Email',
      password: 'Password',
      signIn: 'Sign In',
      forgotPassword: 'Forgot password?',
      demoAccounts: 'Demo Accounts',
      buyer: 'Buyer',
      traveler: 'Traveler'
    },
    ko: {
      title: '로그인',
      subtitle: '계정에 액세스하려면 자격 증명을 입력하세요',
      email: '이메일',
      password: '비밀번호',
      signIn: '로그인',
      forgotPassword: '비밀번호를 잊으셨나요?',
      demoAccounts: '데모 계정',
      buyer: '구매자',
      traveler: '여행자'
    }
  };

  const t = translations?.[currentLanguage];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { error } = await signIn(formData?.email, formData?.password);
      if (error) {
        setError(error?.message);
      } else {
        navigate('/unified-dashboard');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async (email, password) => {
    setFormData({ email, password });
    setError('');
    setLoading(true);

    try {
      const { error } = await signIn(email, password);
      if (error) {
        setError(error?.message);
      } else {
        navigate('/unified-dashboard');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-lg p-6 md:p-8">
      <div className="mb-6">
        <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
          {t?.title}
        </h2>
        <p className="text-sm text-muted-foreground">{t?.subtitle}</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2">
          <Icon name="AlertCircle" size={16} color="var(--color-destructive)" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label={t?.email}
          type="email"
          value={formData?.email}
          onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
          placeholder="you@example.com"
          required
        />

        <Input
          label={t?.password}
          type="password"
          value={formData?.password}
          onChange={(e) => setFormData({ ...formData, password: e?.target?.value })}
          placeholder="••••••••"
          required
        />

        <Button
          type="submit"
          variant="default"
          fullWidth
          loading={loading}
          iconName="LogIn"
          iconPosition="left"
        >
          {t?.signIn}
        </Button>
      </form>

      <div className="mt-6">
        <p className="text-xs text-muted-foreground text-center mb-3">{t?.demoAccounts}</p>
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDemoLogin('buyer@ogada.com', 'Buyer123!')}
            disabled={loading}
          >
            {t?.buyer}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDemoLogin('sarah@ogada.com', 'Traveler123!')}
            disabled={loading}
          >
            {t?.traveler}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDemoLogin('admin@ogada.com', 'Admin123!')}
            disabled={loading}
          >
            Admin
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;